import { logService, LOG_CATEGORIES } from '@/services/logService'
import { showError } from '@/services/toastService'

const OPENED_FILES_EVENT = 'routecamp-opened-files'
export const NATIVE_OPEN_TRACK_FILES_EVENT = 'routecamp-open-track-files'

interface NativeOpenedFile {
  url: string
  name: string
  bytes: number[]
}

interface NativeOpenedFileFailure {
  url: string
  reason: string
}

interface NativeOpenedFilesPayload {
  files: NativeOpenedFile[]
  failures: NativeOpenedFileFailure[]
}

export type NativeOpenTrackFilesEvent = CustomEvent<File[]>

function getTrackFileMimeType(fileName: string): string {
  const normalizedName = fileName.toLowerCase()
  if (normalizedName.endsWith('.gpx')) return 'application/gpx+xml'
  if (normalizedName.endsWith('.fit')) return 'application/vnd.ant.fit'
  return 'application/octet-stream'
}

function toFile(openedFile: NativeOpenedFile): File {
  const type = getTrackFileMimeType(openedFile.name)
  const bytes = Uint8Array.from(openedFile.bytes)
  const blob = new Blob([bytes], { type })
  return new File([blob], openedFile.name, { type })
}

async function takeOpenedFiles(): Promise<NativeOpenedFilesPayload> {
  const { invoke } = await import('@tauri-apps/api/core')
  return await invoke<NativeOpenedFilesPayload>('take_opened_files')
}

async function dispatchOpenedFiles() {
  const payload = await takeOpenedFiles()

  if (payload.failures.length > 0) {
    logService.warn(LOG_CATEGORIES.FILE, 'Failed to read native opened files', {
      failures: payload.failures,
    })
    showError('Some GPX/FIT files could not be opened.')
  }

  if (payload.files.length === 0) return

  const files = payload.files.map(toFile)
  logService.info(LOG_CATEGORIES.FILE, 'Received native opened track files', {
    fileCount: files.length,
  })
  window.dispatchEvent(new CustomEvent(NATIVE_OPEN_TRACK_FILES_EVENT, { detail: files }))
}

export async function setupNativeOpenHandlers() {
  const { listen } = await import('@tauri-apps/api/event')

  await dispatchOpenedFiles()
  await listen(OPENED_FILES_EVENT, () => {
    void dispatchOpenedFiles()
  })
}
